<template>
    <app-modal
        modal-id="super-partner-commissions-modal"
        modal-size="extra-large"
        @close-modal="closeModal">

        <template slot="header">
            <h5 class="modal-title">Comisiones - {{ superPartnerName }}</h5>
            <button type="button" class="close outline-none" @click.prevent="closeModal">
                <span>×</span>
            </button>
        </template>

        <template slot="body">
            <app-overlay-loader v-if="preloader"/>
            <form v-else ref="form" class="mb-0" :class="{'loading-opacity': preloader}">

                <!-- Tabs -->
                <ul class="nav nav-tabs mb-3" role="tablist">
                    <li class="nav-item">
                        <a class="nav-link" :class="{active: activeTab === 'margins'}" href="#" @click.prevent="activeTab = 'margins'">
                            Comisiones (%)
                        </a>
                    </li>
                    <li class="nav-item">
                        <a class="nav-link" :class="{active: activeTab === 'partner'}" href="#" @click.prevent="activeTab = 'partner'">
                            Comisiones por Venta
                        </a>
                    </li>
                    <li class="nav-item">
                        <a class="nav-link" :class="{active: activeTab === 'prices'}" href="#" @click.prevent="activeTab = 'prices'">
                            Precios Fijos (USD)
                        </a>
                    </li>
                    <li class="nav-item">
                        <a class="nav-link" :class="{active: activeTab === 'country'}" href="#" @click.prevent="activeTab = 'country'">
                            Precios por País
                        </a>
                    </li>
                </ul>

                <!-- Tab: Margins -->
                <div v-show="activeTab === 'margins'">
                    <div class="form-group mb-4">
                        <div class="alert alert-info">
                            <strong>Configuración de Comisiones del Super Partner</strong>
                            <p class="mb-0 mt-2">
                                Ajusta la comisión general y la tarifa de eSIM gratuita para este super partner.
                            </p>
                        </div>
                    </div>

                    <div class="form-group row align-items-center">
                        <label for="sp_free_esim_rate" class="col-sm-5 mb-0">
                            Tarifa eSIM Gratuita 1GB (USD)
                        </label>
                        <div class="col-sm-7">
                            <div class="input-group" style="max-width: 220px;">
                                <app-input
                                    id="sp_free_esim_rate"
                                    type="number"
                                    v-model="freeEsimRate"
                                    :min="0"
                                    step="0.01"/>
                            </div>
                            <small class="text-muted d-block mt-1">
                                Monto de referencia por cada eSIM gratuita 1GB (cuando no hay precio fijo configurado).
                            </small>
                        </div>
                    </div>

                    <div class="form-group mt-4">
                        <label class="d-block mb-2">Comisiones por plan (3GB, 5GB, 10GB)</label>
                        <div class="table-responsive">
                            <table class="table table-hover table-sm">
                                <thead>
                                    <tr>
                                        <th>Plan</th>
                                        <th>Comisión %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="plan in marginCapacities" :key="plan">
                                        <td class="align-middle"><strong>{{ plan }}GB</strong></td>
                                        <td class="align-middle">
                                            <div class="input-group" style="max-width: 180px;">
                                                <app-input
                                                    type="number"
                                                    v-model="margins[plan].margin_percentage"
                                                    :min="0"
                                                    :max="100"
                                                    step="0.01"/>
                                                <div class="input-group-append">
                                                    <span class="input-group-text">%</span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Tab: Sale Commissions (real – what we pay the super partner per paid eSIM) -->
                <div v-show="activeTab === 'partner'">
                    <div class="form-group mb-4">
                        <div class="alert alert-info">
                            <strong>Comisiones por Venta de eSIMs de Pago (Solo Admin)</strong>
                            <p class="mb-0 mt-2">
                                Define qué porcentaje de comisión se le paga a este super partner por cada eSIM de plan de pago vendida.
                                <strong>No afecta el precio final al cliente.</strong>
                                Estos valores se usan en el Estado de Cuenta para calcular cuánto les debemos por ventas.
                            </p>
                        </div>
                    </div>

                    <div class="form-group row align-items-center mb-3">
                        <label for="sp_commission_latam" class="col-sm-5 mb-0">
                            Comisión Latam y Europa (%)
                        </label>
                        <div class="col-sm-7">
                            <div class="input-group" style="max-width: 220px;">
                                <app-input
                                    id="sp_commission_latam"
                                    type="number"
                                    v-model="saleCommissionLatamPct"
                                    :min="0"
                                    :max="100"
                                    step="0.01"
                                    :placeholder="'Sin configurar'"/>
                                <div class="input-group-append">
                                    <span class="input-group-text">%</span>
                                </div>
                            </div>
                            <small class="text-muted d-block mt-1">
                                % de comisión a pagar por ventas en Latinoamérica y Europa. Se refleja en el Estado de Cuenta.
                            </small>
                        </div>
                    </div>

                    <div class="form-group row align-items-center">
                        <label for="sp_commission_usa_ca" class="col-sm-5 mb-0">
                            Comisión Canadá y USA (%)
                        </label>
                        <div class="col-sm-7">
                            <div class="input-group" style="max-width: 220px;">
                                <app-input
                                    id="sp_commission_usa_ca"
                                    type="number"
                                    v-model="saleCommissionUsaCaEuPct"
                                    :min="0"
                                    :max="100"
                                    step="0.01"
                                    :placeholder="'Sin configurar'"/>
                                <div class="input-group-append">
                                    <span class="input-group-text">%</span>
                                </div>
                            </div>
                            <small class="text-muted d-block mt-1">
                                % de comisión a pagar por ventas en Canadá y USA. Se refleja en el Estado de Cuenta.
                            </small>
                        </div>
                    </div>
                </div>

                <!-- Tab: Fixed Plan Prices -->
                <div v-show="activeTab === 'prices'">
                    <div class="alert alert-warning">
                        <strong>Precios Fijos por Plan</strong>
                        <p class="mb-0 mt-2">
                            Si asignas un precio fijo para un plan, ese monto se usará directamente al cobrar por una eSIM gratuita de esa capacidad, 
                            ignorando el cálculo de porcentaje. Deja en blanco para seguir usando el sistema de porcentajes.
                        </p>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-hover table-sm">
                            <thead>
                                <tr>
                                    <th>Plan</th>
                                    <th>Precio Fijo (USD)</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="plan in allCapacities" :key="plan">
                                    <td class="align-middle"><strong>{{ plan }}GB</strong></td>
                                    <td class="align-middle">
                                        <div class="input-group" style="max-width: 180px;">
                                            <div class="input-group-prepend">
                                                <span class="input-group-text">$</span>
                                            </div>
                                            <app-input
                                                type="number"
                                                v-model="planPrices[plan].price"
                                                :placeholder="'Sin precio fijo'"
                                                :min="0"
                                                step="0.01"/>
                                        </div>
                                    </td>
                                    <td class="align-middle">
                                        <button type="button" class="btn btn-sm btn-outline-danger" @click="clearPlanPrice(plan)">
                                            <app-icon name="x" style="width:14px;height:14px;"/>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Tab: Country-Specific Prices -->
                <div v-show="activeTab === 'country'">
                    <div class="alert alert-info">
                        <strong>Precios por País</strong>
                        <p class="mb-0 mt-2">
                            Estos precios tienen la mayor prioridad. Déjalos en blanco para usar el porcentaje predeterminado.
                        </p>
                    </div>

                    <div class="table-responsive mb-3">
                        <table class="table table-sm table-hover">
                            <thead>
                                <tr>
                                    <th>País (Código)</th>
                                    <th v-for="capacity in allCapacities" :key="capacity">{{ capacity }}GB (USD)</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(entry, idx) in countryPrices" :key="idx">
                                    <td class="align-middle">
                                        <input class="form-control form-control-sm"
                                               v-model="entry.country_code"
                                               :list="`super-partner-country-options-${idx}`"
                                               placeholder="Escribe un país o código"
                                               style="min-width: 240px; max-width: 320px;"
                                               @change="normalizeCountryCode(entry)"/>
                                        <datalist :id="`super-partner-country-options-${idx}`">
                                            <option v-for="country in countryOptions" :key="country.code" :value="country.code" :label="country.name"/>
                                        </datalist>
                                    </td>
                                    <td v-for="capacity in allCapacities" :key="capacity" class="align-middle">
                                        <div class="input-group" style="min-width: 130px;">
                                            <div class="input-group-prepend">
                                                <span class="input-group-text">$</span>
                                            </div>
                                            <app-input type="number"
                                                       v-model="entry.prices[capacity]"
                                                       :placeholder="'Porcentaje'"
                                                       :min="0"
                                                       step="0.01"/>
                                        </div>
                                    </td>
                                    <td class="align-middle">
                                        <button type="button" class="btn btn-sm btn-outline-danger" @click="removeCountryPrice(idx)">
                                            <app-icon name="trash-2" style="width:14px;height:14px;"/>
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="countryPrices.length === 0">
                                    <td :colspan="allCapacities.length + 2" class="text-center text-muted py-3">
                                        No hay precios por país configurados.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="addCountryPrice">
                        <app-icon name="plus" style="width:14px;height:14px;" class="mr-1"/> Agregar precio por país
                    </button>
                </div>

                <div class="mt-4 d-flex justify-content-end">
                    <button class="btn btn-light mr-2" @click.prevent="closeModal">
                        {{ $t('cancel') }}
                    </button>
                    <button class="btn btn-primary" @click.prevent="submit">
                        {{ $t('save') }}
                    </button>
                </div>
            </form>
        </template>
    </app-modal>
</template>

<script>
    import axios from 'axios';
    import {FormMixin} from '../../../../../core/mixins/form/FormMixin.js';
    import {ModalMixin} from "../../../../Mixins/ModalMixin";

    export default {
        name: 'SuperPartnerCommissionsModal',
        mixins: [FormMixin, ModalMixin],
        props: {
            superPartnerId: {
                type: Number,
                required: true,
            },
            superPartnerName: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                preloader: false,
                activeTab: 'margins',
                commissionPercentage: 0.00,
                freeEsimRate: 0.85,
                saleCommissionLatamPct: null,
                saleCommissionUsaCaEuPct: null,
                allCapacities: ['1', '3', '5', '10'],
                marginCapacities: ['3', '5', '10'],
                margins: {
                    '3': { margin_percentage: 0.00, is_active: true },
                    '5': { margin_percentage: 0.00, is_active: true },
                    '10': { margin_percentage: 0.00, is_active: true },
                },
                planPrices: {
                    '1': { price: '', is_active: true },
                    '3': { price: '', is_active: true },
                    '5': { price: '', is_active: true },
                    '10': { price: '', is_active: true },
                },
                countryOptions: [],
                countryPrices: [],
            };
        },
        mounted() {
            this.fetchData();
        },
        methods: {
            fetchData() {
                this.preloader = true;
                axios.get(`/super-partners/${this.superPartnerId}/commissions`)
                    .then(response => {
                        if (response.data) {
                            if (typeof response.data.commission_percentage !== 'undefined' && response.data.commission_percentage !== null) {
                                this.commissionPercentage = parseFloat(response.data.commission_percentage) || 0.00;
                            }
                            if (typeof response.data.free_esim_rate !== 'undefined' && response.data.free_esim_rate !== null) {
                                this.freeEsimRate = parseFloat(response.data.free_esim_rate) || 0.85;
                            }
                            if (typeof response.data.sale_commission_latam_pct !== 'undefined') {
                                this.saleCommissionLatamPct = response.data.sale_commission_latam_pct !== null
                                    ? parseFloat(response.data.sale_commission_latam_pct)
                                    : null;
                            }
                            if (typeof response.data.sale_commission_usa_ca_eu_pct !== 'undefined') {
                                this.saleCommissionUsaCaEuPct = response.data.sale_commission_usa_ca_eu_pct !== null
                                    ? parseFloat(response.data.sale_commission_usa_ca_eu_pct)
                                    : null;
                            }
                            if (response.data.margins) {
                                Object.keys(response.data.margins).forEach(plan => {
                                    if (this.margins[plan]) {
                                        this.margins[plan] = { ...this.margins[plan], ...response.data.margins[plan] };
                                    }
                                });
                            }
                            if (response.data.plan_prices) {
                                Object.keys(response.data.plan_prices).forEach(plan => {
                                    if (this.planPrices[plan]) {
                                        this.planPrices[plan] = { ...this.planPrices[plan], ...response.data.plan_prices[plan] };
                                    }
                                });
                            }
                            if (response.data.country_prices) {
                                this.countryPrices = this.groupCountryPrices(response.data.country_prices);
                            }
                            if (response.data.countries) {
                                this.countryOptions = response.data.countries
                                    .map(country => ({ code: country.code, name: country.name }))
                                    .sort((left, right) => left.name.localeCompare(right.name));
                            }
                        }
                    })
                    .catch(error => {
                        const message = error.response?.data?.message || this.$t('error_loading_margins');
                        this.$toastr.e(message);
                    })
                    .finally(() => {
                        this.preloader = false;
                    });
            },
            submit() {
                this.preloader = true;
                const payload = {
                    commission_percentage: this.commissionPercentage,
                    free_esim_rate: this.freeEsimRate,
                    sale_commission_latam_pct: this.saleCommissionLatamPct !== '' ? this.saleCommissionLatamPct : null,
                    sale_commission_usa_ca_eu_pct: this.saleCommissionUsaCaEuPct !== '' ? this.saleCommissionUsaCaEuPct : null,
                    margins: this.margins,
                    plan_prices: this.planPrices,
                    country_prices: this.flattenCountryPrices(),
                };

                axios.post(`/super-partners/${this.superPartnerId}/commissions`, payload)
                    .then(response => {
                        const message = response.data?.message || this.$t('updated_response');
                        this.$toastr.s(message);
                        if (response.data) {
                            if (typeof response.data.commission_percentage !== 'undefined' && response.data.commission_percentage !== null) {
                                this.commissionPercentage = parseFloat(response.data.commission_percentage) || this.commissionPercentage;
                            }
                            if (typeof response.data.free_esim_rate !== 'undefined' && response.data.free_esim_rate !== null) {
                                this.freeEsimRate = parseFloat(response.data.free_esim_rate) || this.freeEsimRate;
                            }
                            if (typeof response.data.sale_commission_latam_pct !== 'undefined') {
                                this.saleCommissionLatamPct = response.data.sale_commission_latam_pct !== null
                                    ? parseFloat(response.data.sale_commission_latam_pct)
                                    : null;
                            }
                            if (typeof response.data.sale_commission_usa_ca_eu_pct !== 'undefined') {
                                this.saleCommissionUsaCaEuPct = response.data.sale_commission_usa_ca_eu_pct !== null
                                    ? parseFloat(response.data.sale_commission_usa_ca_eu_pct)
                                    : null;
                            }
                            if (response.data.margins) {
                                Object.keys(response.data.margins).forEach(plan => {
                                    if (this.margins[plan]) {
                                        this.margins[plan] = { ...this.margins[plan], ...response.data.margins[plan] };
                                    }
                                });
                            }
                            if (response.data.plan_prices) {
                                Object.keys(response.data.plan_prices).forEach(plan => {
                                    if (this.planPrices[plan]) {
                                        this.planPrices[plan] = { ...this.planPrices[plan], ...response.data.plan_prices[plan] };
                                    }
                                });
                            }
                            if (response.data.country_prices) {
                                this.countryPrices = this.groupCountryPrices(response.data.country_prices);
                            }
                            if (response.data.countries) {
                                this.countryOptions = response.data.countries
                                    .map(country => ({ code: country.code, name: country.name }))
                                    .sort((left, right) => left.name.localeCompare(right.name));
                            }
                        }
                        setTimeout(() => { this.closeModal(); }, 800);
                    })
                    .catch(error => {
                        const message = error.response?.data?.message || this.$t('error_updating_margins');
                        this.$toastr.e(message);
                    })
                    .finally(() => {
                        this.preloader = false;
                    });
            },
            clearPlanPrice(plan) {
                this.planPrices[plan].price = '';
            },
            addCountryPrice() {
                this.countryPrices.push({ country_code: '', prices: this.emptyCountryPrices() });
            },
            removeCountryPrice(idx) {
                this.countryPrices.splice(idx, 1);
            },
            emptyCountryPrices() {
                return this.allCapacities.reduce((prices, capacity) => {
                    prices[capacity] = '';
                    return prices;
                }, {});
            },
            groupCountryPrices(countryPrices) {
                const grouped = {};

                countryPrices.forEach(item => {
                    if (!grouped[item.country_code]) {
                        grouped[item.country_code] = {
                            country_code: item.country_code,
                            prices: this.emptyCountryPrices(),
                        };
                    }

                    grouped[item.country_code].prices[item.plan_capacity] = item.price !== null ? item.price : '';
                });

                return Object.values(grouped);
            },
            flattenCountryPrices() {
                return this.countryPrices.flatMap(entry => this.allCapacities
                    .filter(capacity => entry.country_code && entry.prices[capacity] !== '' && entry.prices[capacity] !== null)
                    .map(capacity => ({
                        country_code: entry.country_code.trim().toUpperCase(),
                        plan_capacity: capacity,
                        price: entry.prices[capacity],
                    })));
            },
            normalizeCountryCode(entry) {
                const selectedCountry = this.countryOptions.find(country =>
                    country.name.toLowerCase() === entry.country_code.trim().toLowerCase() ||
                    country.code.toLowerCase() === entry.country_code.trim().toLowerCase()
                );

                entry.country_code = selectedCountry ? selectedCountry.code : entry.country_code.trim().toUpperCase();
            },
            closeModal() {
                this.$emit('close');
            },
        },
    };
</script>
